import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import ClipboardService from '../services/clipboardService';
import savedConfigsService from '../services/savedConfigsService';

export default function SaveConfigDialog({ open, onClose, config, onSaved, isManager }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hash, setHash] = useState('');
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [updated, setUpdated] = useState(false);
  const [makePublic, setMakePublic] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      // Save to database using the new service
      const result = await savedConfigsService.saveConfig(
        config,
        name,
        isManager ? makePublic : false
      );
      
      setHash(result.configId);
      setLink(result.url);
      setCopied(false);
      setUpdated(false);
      if (onSaved) onSaved(result.configId);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    try {
      const success = await ClipboardService.copyToClipboard(link);
      if (success) {
        setCopied(true);
      }
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save & Share Configuration</DialogTitle>
      <DialogContent>
        {!hash ? (
          <>
            <TextField
              label="Configuration Name"
              fullWidth
              value={name}
              onChange={e => setName(e.target.value)}
              sx={{ mb: 2 }}
              autoFocus
            />
            {isManager && (
              <FormControlLabel
                control={<Checkbox checked={makePublic} onChange={e => setMakePublic(e.target.checked)} />}
                label="Make Public (visible to all users)"
                sx={{ mb: 2 }}
              />
            )}
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!name || saving}
              fullWidth
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
            {isManager && makePublic && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This configuration will be visible to all users in the Public Configurations list.
              </Alert>
            )}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </>
        ) : (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Configuration saved!
            </Alert>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Shareable Link:
            </Typography>
            <TextField
              value={link}
              fullWidth
              InputProps={{ readOnly: true }}
              sx={{ mb: 2 }}
            />
            <Button variant="outlined" onClick={handleCopy} fullWidth>
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
            {updated && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <b>Updated:</b> This configuration was edited after sharing.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
} 